import { ActionIcon, Button } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { useShallow } from 'zustand/shallow';

import { HistoryPanelList } from 'app/modules/wallforming/components/placement-history/HistoryPanelList';
import { HistoryDialog } from 'app/modules/wallforming/components/placement-history/types/historyDialog';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { DEFAULT_HISTORY_SETTINGS } from 'app/modules/wallforming/components/state-management/placement/states/historyState';

import 'app/modules/wallforming/custom-styles/customHistoryStyle.css';

const stopPropagation = (event: { stopPropagation: () => void }) => event.stopPropagation();

export const HistoryPanel = () => {
    const { t } = useTranslation();
    const panelRef = useRef<HTMLDivElement | null>(null);

    const history = usePlacementStore(
        useShallow((state) => ({
            dialog: state.historyDialog,
            setDialog: state.setHistoryDialog,
            entries: state.undoHistory,
            repeats: state.repeatHistory,
            position: state.historyPosition,
            bytes: state.historyBytes,
            setSettings: state.setHistorySettings
        }))
    );

    const panel = useMemo(() => {
        const width = Math.min(480, window.innerWidth - 24);
        const height = Math.min(440, window.innerHeight - 24);
        return { width, height, x: (window.innerWidth - width) / 2, y: (window.innerHeight - height) / 2 };
    }, []);

    const handleOnClose = useCallback(() => history.setDialog(null), [history]);

    const title = useMemo(
        () =>
            history.dialog === HistoryDialog.UNDO
                ? 'undoHistory'
                : history.dialog === HistoryDialog.REPEAT
                  ? 'repeatHistory'
                  : 'historySettings',
        [history.dialog]
    );

    useEffect(() => {
        if (history.dialog !== null) {
            panelRef.current?.focus();
        }
    }, [history.dialog]);

    return (
        <div
            className="pointer-events-none fixed inset-0"
            style={{ zIndex: 9999, visibility: history.dialog !== null ? 'visible' : 'hidden' }}
            onClick={stopPropagation}
            onPointerDown={stopPropagation}
            onPointerMove={stopPropagation}
            onPointerUp={stopPropagation}
        >
            <Rnd
                bounds="window"
                cancel=".history-panel-close"
                default={panel}
                dragHandleClassName="history-panel-drag-handle"
                enableResizing={false}
                enableUserSelectHack={false}
                style={{ pointerEvents: 'auto' }}
            >
                <div
                    ref={panelRef}
                    aria-label={t(title)}
                    aria-modal={false}
                    className="history-panel"
                    role="dialog"
                    tabIndex={-1}
                    data-history-panel
                    data-input-ui
                >
                    <header className="history-panel-drag-handle history-panel-header">
                        <span className="history-panel-title">
                            <Icon
                                aria-hidden="true"
                                icon={
                                    history.dialog === HistoryDialog.SETTINGS
                                        ? 'settings'
                                        : history.dialog === HistoryDialog.REPEAT
                                          ? 'repeat'
                                          : 'history'
                                }
                                size="sm"
                            />
                            {t(title)}
                        </span>
                        <ActionIcon
                            aria-label={t('close')}
                            className="history-panel-close action-icon-small bg-background-gray-ultra-light hover:bg-background-gray! icon-sm"
                            variant="subtle"
                            onClick={handleOnClose}
                            onMouseDown={stopPropagation}
                        >
                            close
                        </ActionIcon>
                    </header>

                    <HistoryPanelList />

                    <footer className="history-panel-footer">
                        <span className="history-panel-usage">
                            {history.dialog === HistoryDialog.REPEAT
                                ? t('historyRepeatUsage', { steps: history.repeats.length })
                                : t('historyUsage', {
                                      steps: history.entries.length,
                                      memory: (history.bytes / (1024 * 1024)).toFixed(2)
                                  })}
                        </span>
                        {history.dialog === HistoryDialog.SETTINGS && (
                            <Button
                                size={sizes.button.small}
                                variant={variants.button.secondary}
                                onClick={() => history.setSettings(DEFAULT_HISTORY_SETTINGS)}
                            >
                                {t('historyResetDefaults')}
                            </Button>
                        )}
                    </footer>
                </div>
            </Rnd>
        </div>
    );
};
